import React from 'react'
import {
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  Checkbox,
  IconButton,
  Box,
  Typography,
  Skeleton,
  TablePagination,
  Chip,
} from '@mui/material'
// Removed unused imports

export interface TableColumn {
  id: string
  label: string
  width?: string | number
  align?: 'left' | 'center' | 'right'
  sortable?: boolean
  render?: (value: any, row: any) => React.ReactNode
}

export interface TableAction {
  icon: React.ReactElement
  label: string
  onClick: (row: any) => void
  color?: 'primary' | 'secondary' | 'error' | 'warning' | 'info' | 'success'
}

interface DataTableProps {
  columns: TableColumn[]
  rows: any[]
  selectedRows?: string[]
  onSelectRow?: (id: string) => void
  onSelectAllRows?: (selected: boolean) => void
  actions?: TableAction[]
  isLoading?: boolean
  emptyMessage?: string
  page?: number
  rowsPerPage?: number
  totalRows?: number
  onPageChange?: (page: number) => void
  onRowsPerPageChange?: (rowsPerPage: number) => void
  rowsPerPageOptions?: number[]
  showPagination?: boolean
  getRowId?: (row: any) => string
  getRowStyle?: (row: any) => React.CSSProperties
  enableSelection?: boolean
  /** Use MUI's compact cell padding -- opt-in per page so other tables keep their current spacing. */
  dense?: boolean
}

const DataTable: React.FC<DataTableProps> = ({
  columns,
  rows,
  selectedRows = [],
  onSelectRow,
  onSelectAllRows,
  actions = [],
  isLoading = false,
  emptyMessage = 'No data available',
  page = 0,
  rowsPerPage = 10,
  totalRows,
  onPageChange,
  onRowsPerPageChange,
  rowsPerPageOptions = [10, 25, 50],
  showPagination = true,
  getRowId = (row) => row.id,
  getRowStyle,
  enableSelection = false,
  dense = false,
}) => {
  const hasSelection = enableSelection && onSelectRow && onSelectAllRows
  const hasActions = actions && actions.length > 0
  const isAllSelected = rows && rows.length > 0 && selectedRows.length === rows.length
  const isIndeterminate = selectedRows.length > 0 && rows && selectedRows.length < rows.length

  const handleSelectAll = (event: React.ChangeEvent<HTMLInputElement>) => {
    if (onSelectAllRows) {
      onSelectAllRows(event.target.checked)
    }
  }

  const handleSelectRow = (id: string) => {
    if (onSelectRow) {
      onSelectRow(id)
    }
  }

  const renderCell = (column: TableColumn, row: any) => {
    const value = row[column.id]
    
    if (column.render) {
      return column.render(value, row)
    }

    // Default rendering for common data types
    if (typeof value === 'boolean') {
      return (
        <Chip 
          label={value ? 'Yes' : 'No'} 
          color={value ? 'success' : 'default'}
          size="small"
          variant="outlined"
        />
      )
    }

    if (typeof value === 'number' && column.id.toLowerCase().includes('price')) {
      return (
        <Typography variant="body2" fontWeight={500}>
          ₹{value.toLocaleString('en-IN')}
        </Typography>
      )
    }

    return value
  }

  if (isLoading) {
    return (
      <TableContainer component={Paper} sx={{ borderRadius: 2 }}>
        <Table size={dense ? 'small' : 'medium'}>
          <TableHead>
            <TableRow sx={{ backgroundColor: '#0d47a1' }}>
              {hasSelection && (
                <TableCell padding="checkbox">
                  <Skeleton variant="rectangular" width={20} height={20} />
                </TableCell>
              )}
              {columns.map((column) => (
                <TableCell 
                  key={column.id}
                  align={column.align || 'left'}
                  sx={{ 
                    color: '#ffffff',
                    fontWeight: 600,
                    fontSize: '0.875rem'
                  }}
                >
                  <Skeleton variant="text" />
                </TableCell>
              ))}
              {hasActions && (
                <TableCell align="right" sx={{ color: '#ffffff', fontWeight: 600 }}>
                  <Skeleton variant="text" />
                </TableCell>
              )}
            </TableRow>
          </TableHead>
          <TableBody>
            {Array.from({ length: rowsPerPage }).map((_, index) => (
              <TableRow key={index}>
                {hasSelection && (
                  <TableCell padding="checkbox">
                    <Skeleton variant="rectangular" width={20} height={20} />
                  </TableCell>
                )}
                {columns.map((column) => (
                  <TableCell key={column.id}>
                    <Skeleton variant="text" />
                  </TableCell>
                ))}
                {hasActions && (
                  <TableCell>
                    <Skeleton variant="circular" width={24} height={24} />
                  </TableCell>
                )}
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </TableContainer>
    )
  }

  return (
    <Box>
      <TableContainer component={Paper} sx={{ borderRadius: 2, boxShadow: '0 2px 8px rgba(0,0,0,0.1)' }}>
        <Table size={dense ? 'small' : 'medium'}>
          <TableHead>
            <TableRow sx={{ backgroundColor: '#0d47a1' }}>
              {hasSelection && (
                <TableCell padding="checkbox">
                  <Checkbox
                    indeterminate={isIndeterminate}
                    checked={isAllSelected}
                    onChange={handleSelectAll}
                    sx={{ color: '#ffffff' }}
                  />
                </TableCell>
              )}
              {columns.map((column) => (
                <TableCell 
                  key={column.id}
                  align={column.align || 'left'}
                  sx={{ 
                    width: column.width,
                    color: '#ffffff',
                    fontWeight: 600,
                    fontSize: '0.875rem'
                  }}
                >
                  {column.label}
                </TableCell>
              ))}
              {hasActions && (
                <TableCell 
                  align="right" 
                  sx={{ 
                    color: '#ffffff', 
                    fontWeight: 600,
                    width: 100
                  }}
                >
                  Actions
                </TableCell>
              )}
            </TableRow>
          </TableHead>
          <TableBody>
            {rows.length === 0 ? (
              <TableRow>
                <TableCell 
                  colSpan={columns.length + (hasSelection ? 1 : 0) + (hasActions ? 1 : 0)} 
                  align="center" 
                  sx={{ py: 4 }}
                >
                  <Typography variant="body1" color="text.secondary">
                    {emptyMessage}
                  </Typography>
                </TableCell>
              </TableRow>
            ) : (
              rows.map((row) => {
                const rowId = getRowId(row)
                const isSelected = selectedRows.includes(rowId)
                
                const customStyle = getRowStyle ? getRowStyle(row) : {}
                
                return (
                  <TableRow
                    key={rowId}
                    hover
                    selected={isSelected}
                    sx={{
                      '&:hover': {
                        backgroundColor: 'rgba(33, 150, 243, 0.04)',
                      },
                      ...customStyle,
                    }}
                  >
                    {hasSelection && (
                      <TableCell padding="checkbox">
                        <Checkbox
                          checked={isSelected}
                          onChange={() => handleSelectRow(rowId)}
                        />
                      </TableCell>
                    )}
                    {columns.map((column) => (
                      <TableCell 
                        key={column.id} 
                        align={column.align || 'left'}
                      >
                        {renderCell(column, row)}
                      </TableCell>
                    ))}
                    {hasActions && (
                      <TableCell align="right">
                        <Box sx={{ display: 'flex', gap: 0.5, justifyContent: 'flex-end' }}>
                          {actions.map((action, index) => (
                            <IconButton
                              key={index}
                              size="small"
                              color={action.color || 'primary'}
                              onClick={() => action.onClick(row)}
                              title={action.label}
                            >
                              {action.icon}
                            </IconButton>
                          ))}
                        </Box>
                      </TableCell>
                    )}
                  </TableRow>
                )
              })
            )}
          </TableBody>
        </Table>
      </TableContainer>

      {showPagination && (
        <TablePagination
          component="div"
          count={totalRows || rows.length}
          page={page}
          onPageChange={(_, newPage) => onPageChange?.(newPage)}
          rowsPerPage={rowsPerPage}
          onRowsPerPageChange={(event) => onRowsPerPageChange?.(parseInt(event.target.value, 10))}
          rowsPerPageOptions={rowsPerPageOptions}
          sx={{
            borderTop: 1,
            borderColor: 'divider',
            backgroundColor: 'background.paper',
          }}
        />
      )}
    </Box>
  )
}

export default DataTable